import React from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Image,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import balanceImg from 'assets/img/dashboards/balanceImg.png';
import fakeGraph from 'assets/img/dashboards/fakeGraph.png';
import { MdAdd, MdDownload, MdOutlineMoreHoriz, MdRemove } from 'react-icons/md';
import { getSessionCandidateId } from 'helpers/session';

type BalanceProps = {
  [x: string]: any;
};

export default function Balance(props: BalanceProps) {
  const { ...rest } = props;
  const toast = useToast();
  const {
    isOpen: isOpenMenu,
    onOpen: onOpenMenu,
    onClose: onCloseMenu,
  } = useDisclosure();
  const {
    isOpen: isOpenPositive,
    onOpen: onOpenPositive,
    onClose: onClosePositive,
  } = useDisclosure();
  const {
    isOpen: isOpenNegative,
    onOpen: onOpenNegative,
    onClose: onCloseNegative,
  } = useDisclosure();

  const [availableFunds, setAvailableFunds] = React.useState<number>(0);
  const [positiveAmount, setPositiveAmount] = React.useState('');
  const [positiveConcept, setPositiveConcept] = React.useState('');
  const [negativeAmount, setNegativeAmount] = React.useState('');
  const [negativeConcept, setNegativeConcept] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);

  const balanceBg = useColorModeValue('brand.900', '#1B254B');
  const textColor = useColorModeValue('secondaryGray.500', 'white');
  const textHover = useColorModeValue(
    { color: 'secondaryGray.900', bg: 'unset' },
    { color: 'secondaryGray.500', bg: 'unset' },
  );
  const bgList = useColorModeValue('white', 'whiteAlpha.100');
  const bgShadow = useColorModeValue(
    '14px 17px 40px 4px rgba(112, 144, 176, 0.08)',
    'unset',
  );

  const loadFunds = React.useCallback(async () => {
    try {
      const response = await fetch(process.env.REACT_APP_URL_SERVER + '/fondos', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const result = await response.json();
      setAvailableFunds(Number(result?.data?._sum?.Monto || 0));
    } catch (error) {
      setAvailableFunds(0);
    }
  }, []);

  React.useEffect(() => {
    loadFunds();
    const timer = setInterval(loadFunds, 4000);
    return () => clearInterval(timer);
  }, [loadFunds]);

  const submitAdjustment = async (tipo: 'Ingreso' | 'Egreso') => {
    const candidateId = getSessionCandidateId();
    const amount = tipo === 'Ingreso' ? positiveAmount : negativeAmount;
    const concept = tipo === 'Ingreso' ? positiveConcept : negativeConcept;

    if (!candidateId) {
      toast({ title: 'No se encontro idCandidato', status: 'error', duration: 2500 });
      return;
    }

    if (!amount || Number(amount) <= 0 || !concept.trim()) {
      toast({ title: 'Monto y concepto son obligatorios', status: 'warning', duration: 2500 });
      return;
    }

    setIsSaving(true);
    try {
      const formData = new FormData();
      formData.append('Monto', amount);
      formData.append('Concepto', concept.trim());
      formData.append('Tipo', tipo);
      formData.append('idCandidato', String(candidateId));

      const response = await fetch(process.env.REACT_APP_URL_SERVER + '/reg_fondos', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('No fue posible guardar el ajuste');
      }

      toast({
        title: tipo === 'Ingreso' ? 'Ajuste positivo aplicado' : 'Ajuste negativo aplicado',
        status: 'success',
        duration: 2500,
      });

      if (tipo === 'Ingreso') {
        setPositiveAmount('');
        setPositiveConcept('');
        onClosePositive();
      } else {
        setNegativeAmount('');
        setNegativeConcept('');
        onCloseNegative();
      }

      await loadFunds();
    } catch (error) {
      toast({ title: 'Error al guardar el ajuste', status: 'error', duration: 2500 });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Card flexDirection="column" w="100%" {...rest}>
        <Flex
          justify="space-between"
          p="20px"
          mb="20px"
          borderRadius="16px"
          bgColor={balanceBg}
          bgImage={balanceImg}
          bgPosition="right"
          bgSize="cover"
        >
          <Flex align="center" justify="space-between" w="100%">
            <Flex flexDirection="column" me="20px">
              <Text color="white" fontSize="sm" fontWeight="500">
                Fondos Disponibles:
              </Text>
              <Text color="white" fontSize="34px" fontWeight="700" lineHeight="100%">
                $ {availableFunds.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Text>
              <HStack spacing="10px" mt="16px">
                <Button
                  leftIcon={<Icon as={MdAdd} />}
                  size="sm"
                  bg="whiteAlpha.250"
                  color="white"
                  _hover={{ bg: 'whiteAlpha.350' }}
                  onClick={onOpenPositive}
                >
                  Ajuste Positivo
                </Button>
                <Button
                  leftIcon={<Icon as={MdRemove} />}
                  size="sm"
                  bg="blackAlpha.300"
                  color="white"
                  _hover={{ bg: 'blackAlpha.400' }}
                  onClick={onOpenNegative}
                >
                  Ajuste Negativo
                </Button>
              </HStack>
            </Flex>
            <Flex flexDirection="column" ms="auto" justify="space-between" align="flex-end">
              <Menu isOpen={isOpenMenu} onClose={onCloseMenu}>
                <MenuButton onClick={onOpenMenu}>
                  <Icon
                    cursor="pointer"
                    as={MdOutlineMoreHoriz}
                    color="white"
                    mt="-2px"
                    mb="12px"
                    w="30px"
                    h="30px"
                  />
                </MenuButton>
                <MenuList
                  w="180px"
                  minW="unset"
                  maxW="180px !important"
                  border="transparent"
                  backdropFilter="blur(63px)"
                  bg={bgList}
                  boxShadow={bgShadow}
                  borderRadius="20px"
                  p="15px"
                >
                  <MenuItem
                    transition="0.2s linear"
                    color={textColor}
                    _hover={textHover}
                    p="0px"
                    borderRadius="8px"
                    _active={{ bg: 'transparent' }}
                    _focus={{ bg: 'transparent' }}
                    mb="10px"
                    onClick={onOpenPositive}
                  >
                    <Flex align="center">
                      <Icon as={MdAdd} h="16px" w="16px" me="8px" />
                      <Text fontSize="sm" fontWeight="400">
                        Ajuste positivo
                      </Text>
                    </Flex>
                  </MenuItem>
                  <MenuItem
                    transition="0.2s linear"
                    color={textColor}
                    _hover={textHover}
                    p="0px"
                    borderRadius="8px"
                    _active={{ bg: 'transparent' }}
                    _focus={{ bg: 'transparent' }}
                    mb="10px"
                    onClick={onOpenNegative}
                  >
                    <Flex align="center">
                      <Icon as={MdRemove} h="16px" w="16px" me="8px" />
                      <Text fontSize="sm" fontWeight="400">
                        Ajuste negativo
                      </Text>
                    </Flex>
                  </MenuItem>
                  <MenuItem
                    transition="0.2s linear"
                    color={textColor}
                    _hover={textHover}
                    p="0px"
                    borderRadius="8px"
                    _active={{ bg: 'transparent' }}
                    _focus={{ bg: 'transparent' }}
                  >
                    <Flex align="center">
                      <Icon as={MdDownload} h="16px" w="16px" me="8px" />
                      <Text fontSize="sm" fontWeight="400">
                        Movimientos
                      </Text>
                    </Flex>
                  </MenuItem>
                </MenuList>
              </Menu>
              <Image src={fakeGraph} w="59px" h="17px" />
            </Flex>
          </Flex>
        </Flex>
      </Card>

      <AdjustmentModal
        isOpen={isOpenPositive}
        onClose={onClosePositive}
        title="Ajuste Positivo"
        amount={positiveAmount}
        setAmount={setPositiveAmount}
        concept={positiveConcept}
        setConcept={setPositiveConcept}
        isSaving={isSaving}
        onSubmit={() => submitAdjustment('Ingreso')}
      />

      <AdjustmentModal
        isOpen={isOpenNegative}
        onClose={onCloseNegative}
        title="Ajuste Negativo"
        amount={negativeAmount}
        setAmount={setNegativeAmount}
        concept={negativeConcept}
        setConcept={setNegativeConcept}
        isSaving={isSaving}
        onSubmit={() => submitAdjustment('Egreso')}
      />
    </>
  );
}

function AdjustmentModal(props: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  amount: string;
  setAmount: (value: string) => void;
  concept: string;
  setConcept: (value: string) => void;
  isSaving: boolean;
  onSubmit: () => void;
}) {
  const { isOpen, onClose, title, amount, setAmount, concept, setConcept, isSaving, onSubmit } = props;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay bg="blackAlpha.500" />
      <ModalContent borderRadius="24px">
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <Box>
              <Text fontSize="sm" fontWeight="700" color="gray.500" mb={2}>
                Monto
              </Text>
              <Input
                type="number"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                placeholder="1000"
                borderRadius="16px"
                h="50px"
              />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="700" color="gray.500" mb={2}>
                Concepto
              </Text>
              <Input
                value={concept}
                onChange={(event) => setConcept(event.target.value)}
                placeholder="Operacion territorial"
                borderRadius="16px"
                h="50px"
              />
            </Box>
          </Stack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose}>
            Cerrar
          </Button>
          <Button colorScheme="brand" onClick={onSubmit} isLoading={isSaving}>
            Guardar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
